import {
  Box,
  Chevron,
  Div,
  getUserTransactionPending,
  H5,
  Icon,
  Img,
  isAdmin,
  lightTheme,
  P,
  StatusMark,
  transactionsNoData,
  reportError,
} from '@vgrubs/components';
import { AdminHeader, AdminSpinner } from 'components/src/adminComponents';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getTransactionPendingPartner } from '../../api/wallet.api';
import {
  NewTransactionResponse,
  vGrubsIncomeEntity,
} from '../../types/api/wallet.types';
import { TransactionPending } from '../../types/wallet.types';
import { TransactionStatus } from '../../types/shared.types';
import { IncomeDetailModal } from './DetailModals/IncomeDetailModal';
import {
  CashOrderTag,
  describeIncomeRow,
  TransactionAmount,
} from './TransactionComponents';
import { Collapsible, showTransactionIcon } from './TransactonsStyled';

type GroupedTransactions = {
  date: string;
  transactions: TransactionPending[];
};

export const TransactionsPending = () => {
  const [groupedTransactions, setGroupedTransactions] = useState<
    GroupedTransactions[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [incomeModal, setIncomeModal] =
    useState<NewTransactionResponse<vGrubsIncomeEntity> | null>(null);
  const { id, locationId } = useParams();

  const openIncomeModal = (item: TransactionPending) => {
    if (!item.incomeEntity) return;
    setIncomeModal({
      createdAt: item.incomeEntity.createdAt,
      amount: item.incomeEntity.amount ?? 0,
      status: TransactionStatus.pending,
      object: item.incomeEntity,
    });
  };

  const getTransactions = async () => {
    setLoading(true);

    const getTransactionsFunction = isAdmin
      ? getUserTransactionPending
      : getTransactionPendingPartner;

    try {
      const { data } = await getTransactionsFunction(
        id ?? '',
        locationId ?? ''
      );

      // Group transactions by date
      const transactionsByDate: Record<string, TransactionPending[]> = {};

      data.data.forEach((transaction) => {
        const date = DateTime.fromISO(transaction.availableAt).toISODate();

        if (!transactionsByDate[date!]) {
          transactionsByDate[date!] = [];
        }
        transactionsByDate[date!].push(transaction);
      });

      // Convert to array and sort by date (newest first)
      const grouped = Object.entries(transactionsByDate)
        .map(([date, transactions]) => ({
          date,
          transactions: transactions.sort(
            (a, b) =>
              DateTime.fromISO(b.availableAt).toMillis() -
              DateTime.fromISO(a.availableAt).toMillis()
          ),
        }))
        .sort(
          (a, b) =>
            DateTime.fromISO(b.date).toMillis() -
            DateTime.fromISO(a.date).toMillis()
        );

      setGroupedTransactions(grouped);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTransactions();
  }, []);

  return (
    <>
      <AdminHeader title="Pending Transactions" />
      {loading && <AdminSpinner />}
      <Div $m="32px 0" $mSM="20px 0">
        {groupedTransactions.map((item, index) => (
          <TransactionDateComponent
            key={item.date}
            item={item}
            index={index}
            onRowClick={openIncomeModal}
          />
        ))}

        {groupedTransactions.length < 1 && !loading && (
          <Div
            $flex
            $column
            $alignCenter
            $justifyCenter
            $height="70vh"
            $heightSM="70vh"
          >
            <Img alt="" $width="180px" src={transactionsNoData} />
            <H5 $mt="40" $center>
              No Pending Transactions!
            </H5>
          </Div>
        )}
      </Div>
      <IncomeDetailModal
        modalIsOpen={incomeModal}
        closeModal={() => setIncomeModal(null)}
      />
    </>
  );
};

export const TransactionDateComponent = ({
  item,
  index,
  onRowClick,
}: {
  item: GroupedTransactions;
  index: number;
  onRowClick?: (transaction: TransactionPending) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  const handleExpand = () => {
    setExpanded((prev) => (prev === item.date ? null : item.date));
    setHeight(ref?.current?.clientHeight ?? 0);
  };

  useEffect(() => {
    if (index === 0) {
      setExpanded(item.date);
      setHeight(ref?.current?.clientHeight ?? 0);
    }
    if (expanded) setHeight(ref?.current?.clientHeight ?? 0);
  }, [item, index]);

  const isExpanded = expanded === item.date;

  return (
    <>
      <Box
        $flex
        $alignCenter
        $between
        $mb="8"
        $background={isExpanded && lightTheme.primary}
        $pointer
        onClick={handleExpand}
        $minHeight="40px"
      >
        <Div $flex $alignCenter $gap="8px">
          <P $bold $m="0" $color={isExpanded && lightTheme.white}>
            {DateTime.fromISO(item.date).toFormat('MMMM dd, yyyy')}
          </P>
        </Div>
        <Icon
          $pointer
          $rotate={isExpanded ? '360deg' : '180deg'}
          color={isExpanded ? lightTheme.white : lightTheme.darkGray}
        >
          <Chevron />
        </Icon>
      </Box>

      <Collapsible expanded={isExpanded} elementHeight={height}>
        <div ref={ref}>
          {item?.transactions?.map((item, i) => {
            return (
              <TransactionComponent
                key={i}
                item={item}
                onRowClick={onRowClick}
              />
            );
          })}
        </div>
      </Collapsible>
    </>
  );
};

export const TransactionComponent = ({
  item,
  onRowClick,
}: {
  item: TransactionPending;
  onRowClick?: (transaction: TransactionPending) => void;
}) => {
  const clickable = !!item.incomeEntity && !!onRowClick;
  const { sign, amountColor } = describeIncomeRow(
    item.incomeEntity ?? undefined
  );
  return (
    <Box
      key={item.orderId}
      $flex
      $alignCenter
      $between
      $mb="8"
      $pointer={clickable}
      onClick={clickable ? () => onRowClick?.(item) : undefined}
      $relative
    >
      <StatusMark $color={lightTheme.yellow} />
      <Div $flex $alignCenter $gap="20px" $gapSM="16px">
        {showTransactionIcon('Withdrawals')}
        <Div $width="100%" $pointer={clickable}>
          <Div $flex $alignCenter $gap="8px" $wrap>
            <H5 $m="0">Order ID: {item.orderId}</H5>
            {item.incomeEntity?.cashPayment && <CashOrderTag />}
          </Div>
          <P $color={lightTheme.lightGray} $mb="0">
            {item.locationName}
          </P>
        </Div>
      </Div>
      <Div $gap="24px" $gapSM="12px">
        <TransactionAmount
          amount={item.amount}
          sign={sign}
          color={amountColor}
        />
        <P $right $color={lightTheme.lightGray} $m="0">
          {DateTime.fromISO(item.availableAt).toFormat('hh:mm a')}
        </P>
      </Div>
    </Box>
  );
};
